/* SUMMARY
  * technicianExportDescriptor: mapping data/xlsx
*/

import { t } from "i18next";
import { AutoDiagLatest } from "../../../types/auto-diag/autoDiagSubmission";
import { formatLastDiag } from "../../utils/utils";

/**
 * A technicians-tab row: the technician plus the Auto Diag summary the tab joins onto it for
 * display. `autoDiag` is undefined for a technician who has never submitted — deliberately absent
 * rather than a falsy record, so the export can tell that apart from an invalid licence.
 */
export type TechnicianExportRow = EasyTech.Technician & { autoDiag?: AutoDiagLatest };

/**
* technicianExportDescriptor: mapping data/xlsx
*
* Separate from userExportDescriptor because the technicians tab shows three columns a plain user
* does not have — van, phone and colour — and two of those live under `attributes` rather than on
* the entity itself. Every branch it reads is optional on EasyTech.Technician, so every getter is
* guarded: a technician with no van assigned is ordinary, not exceptional.
*/
export const technicianExportDescriptor: EasyTech.ExportDescriptor[] = [{
  column: "id",
  columnTranslationKey: "common:export.columns.id",
  // Identifiant technique, demandé pour rapprocher un export d'un autre système. Décoché comme
  // n'importe quelle autre colonne si le lecteur n'en a pas l'usage.
  getter: (e: { id?: string }) => e.id || ""
}, {
  column: "lastname",
  columnTranslationKey: "common:lastName",
  getter: (e: EasyTech.Technician) => e.lastName || ""
}, {
  column: "firstname",
  columnTranslationKey: "common:firstName",
  getter: (e: EasyTech.Technician) => e.firstName || ""
}, {
  column: "email",
  columnTranslationKey: "common:email",
  getter: (e: EasyTech.Technician) => e.email || ""
}, {
  column: "phone",
  columnTranslationKey: "common:phone",
  getter: (e: EasyTech.Technician) => e.attributes?.phone || ""
}, {
  column: "van",
  columnTranslationKey: "common:export.columns.van",
  getter: (e: EasyTech.Technician) => e.van?.name || ""
}, {
  column: "lastDiag",
  columnTranslationKey: "common:autoDiag.lastDiag",
  // Mirrors the column: formatted identically, empty when the technician has never submitted.
  getter: (e: TechnicianExportRow) => formatLastDiag(e.autoDiag?.submittedAt) || ""
}, {
  column: "drivingLicence",
  columnTranslationKey: "common:autoDiag.drivingLicence",
  // A spreadsheet cannot carry the icon, and "" would read as invalid — so "never submitted" is
  // an empty cell and the two real states are spelled out.
  getter: (e: TechnicianExportRow) => e.autoDiag === undefined
    ? ""
    : t(e.autoDiag.drivingLicenceValid ? "admin:autoDiag.licenceValid" : "admin:autoDiag.licenceInvalid")
}, {
  column: "roles",
  columnTranslationKey: "common:roles",
  getter: (e: EasyTech.Technician) => (e.roles ?? []).join("/")
}, {
  column: "color",
  columnTranslationKey: "common:color",
  getter: (e: EasyTech.Technician) => e.attributes?.color || ""
}];
/***/
